import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatsCard } from "@/components/stats-card";
import {
  Globe, Activity, Zap, AlertTriangle, Loader2, RefreshCcw,
  ChevronDown, ChevronUp, Play, Search, CheckCircle2, XCircle, Star, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  crawlStrategyApi,
  type CrawlStrategyConfig,
  type CrawlAttemptLog,
  type DomainStats,
  type MatrixRow,
  MATRIX_STRATEGY_LABELS,
} from "@/api/crawl-strategy";
import { tasksApi, type DomainStats as TaskDomainStats } from "@/api/tasks";

// ── Matrix Strategy Column Definitions ──
const MATRIX_COLS = Object.keys(MATRIX_STRATEGY_LABELS) as Array<keyof typeof MATRIX_STRATEGY_LABELS>;

function MatrixCell({ status, isPinned, onClick }: { status: 'V' | 'X' | '—'; isPinned: boolean; onClick?: () => void }) {
  const base = "w-12 h-7 text-xs font-bold flex items-center justify-center rounded cursor-pointer select-none transition-colors";
  if (status === 'V') return (
    <div
      className={`${base} bg-green-100 text-green-700 hover:bg-green-200 ${isPinned ? 'ring-2 ring-yellow-400' : ''}`}
      onClick={onClick}
      title={isPinned ? "已釘選 (點擊解除)" : "成功 (點擊釘選)"}
    >
      V
    </div>
  );
  if (status === 'X') return (
    <div className={`${base} bg-red-100 text-red-600 cursor-default`} title="全部失敗">X</div>
  );
  return <div className={`${base} text-gray-300 cursor-default`}>—</div>;
}

// ── Strategy badge colors ──

const STRATEGY_COLORS: Record<string, string> = {
  quickscraper: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  quickscraper_premium: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  browseract: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  direct: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
};

const STATUS_COLORS: Record<string, string> = {
  success: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  partial: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  failed: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  timeout: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  blocked: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(ms: number | null): string {
  if (ms === null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ── Main page ──

export default function CrawlStrategyManager() {
  const queryClient = useQueryClient();
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [expandedHistoryDomain, setExpandedHistoryDomain] = useState<string | null>(null);
  const [testUrl, setTestUrl] = useState("");
  const [testDomain, setTestDomain] = useState("");
  const [filterText, setFilterText] = useState("");
  const [historyFilter, setHistoryFilter] = useState("");
  const [matrixFilter, setMatrixFilter] = useState("");
  const [resetAllConfirm, setResetAllConfirm] = useState(false);

  // Queries
  const { data: configs, isLoading: configsLoading } = useQuery({
    queryKey: ["crawl-strategies"],
    queryFn: async () => (await crawlStrategyApi.getAll()).data!,
  });

  const { data: stats } = useQuery({
    queryKey: ["crawl-strategy-stats"],
    queryFn: async () => (await crawlStrategyApi.getStats()).data!,
  });

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ["crawl-strategy-logs", expandedDomain],
    queryFn: async () => {
      if (!expandedDomain) return [];
      return (await crawlStrategyApi.getLogs(expandedDomain, 30)).data!;
    },
    enabled: !!expandedDomain,
  });

  // Domain crawl history from domain_strategy_cache
  const { data: domainHistory = [] } = useQuery({
    queryKey: ["domain-history-stats"],
    queryFn: async () => (await tasksApi.domainStats()).data ?? [],
  });

  // Strategy Matrix data (30s refresh)
  const { data: matrixData = [], isLoading: matrixLoading } = useQuery({
    queryKey: ["crawl-strategy-matrix"],
    queryFn: async () => {
      const res = await crawlStrategyApi.getMatrix();
      return res.data ?? [];
    },
    refetchInterval: 30000,
  });

  // Mutations
  const testMutation = useMutation({
    mutationFn: ({ domain, url }: { domain: string; url: string }) =>
      crawlStrategyApi.testUrl(domain, url),
    onSuccess: (res) => {
      const d = res.data!;
      toast.success(`Test: ${d.status} | ${formatBytes(d.html_size)} | ${formatTime(d.response_time_ms)}`);
      queryClient.invalidateQueries({ queryKey: ["crawl-strategies"] });
      queryClient.invalidateQueries({ queryKey: ["crawl-strategy-logs"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const autoDetectMutation = useMutation({
    mutationFn: () => crawlStrategyApi.autoDetect(),
    onSuccess: (res) => {
      const results = res.data!;
      if (results.length === 0) {
        toast.info("No domains with 10+ attempts yet");
      } else {
        toast.success(`Updated ${results.length} domain(s): ${results.map(r => `${r.domain}→${r.best_strategy}`).join(", ")}`);
      }
      queryClient.invalidateQueries({ queryKey: ["crawl-strategies"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const clearAllPausesMutation = useMutation({
    mutationFn: () => tasksApi.clearAllDomainPauses(),
    onSuccess: (res) => {
      toast.success(`已解除 ${res.data?.cleared ?? 0} 個網域的封鎖`);
      queryClient.invalidateQueries({ queryKey: ["crawl-strategies"] });
    },
    onError: (err: Error) => toast.error(`解除失敗: ${err.message}`),
  });

  const resetAllMutation = useMutation({
    mutationFn: () => crawlStrategyApi.resetAll(),
    onSuccess: (res) => {
      const d = res.data!;
      toast.success(`已清空：${d.cleared_cache} 筆快取、${d.cleared_pauses} 個暫停、${d.cleared_configs} 個釘選`);
      setResetAllConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["crawl-strategy-matrix"] });
      queryClient.invalidateQueries({ queryKey: ["domain-history-stats"] });
    },
    onError: (err: Error) => toast.error(`清空失敗: ${err.message}`),
  });

  const pinMutation = useMutation({
    mutationFn: ({ domain, strategy_name }: { domain: string; strategy_name: string }) =>
      crawlStrategyApi.pinStrategy(domain, strategy_name),
    onSuccess: (_, vars) => {
      toast.success(`已釘選 ${vars.domain} → ${vars.strategy_name}`);
      queryClient.invalidateQueries({ queryKey: ["crawl-strategy-matrix"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const unpinMutation = useMutation({
    mutationFn: (domain: string) => crawlStrategyApi.unpinStrategy(domain),
    onSuccess: (_, domain) => {
      toast.success(`已解除 ${domain} 釘選`);
      queryClient.invalidateQueries({ queryKey: ["crawl-strategy-matrix"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const resetDomainMutation = useMutation({
    mutationFn: (domain: string) => crawlStrategyApi.resetDomain(domain),
    onSuccess: (_, domain) => {
      toast.success(`已重置 ${domain}`);
      queryClient.invalidateQueries({ queryKey: ["crawl-strategy-matrix"] });
      queryClient.invalidateQueries({ queryKey: ["domain-history-stats"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Compute overview stats
  const totalDomains = stats?.length ?? 0;
  const avgSuccessRate = stats && stats.length > 0
    ? (stats.reduce((sum, s) => sum + Number(s.avg_success_rate), 0) / stats.length).toFixed(1)
    : "0";
  const todayAttempts = stats?.reduce((sum, s) => sum + Number(s.today_attempts), 0) ?? 0;
  const todayFailures = stats?.reduce((sum, s) => sum + Number(s.today_failures), 0) ?? 0;
  const failureRate = todayAttempts > 0 ? ((todayFailures / todayAttempts) * 100).toFixed(1) : "0";

  // Group configs by domain
  const domainMap = new Map<string, CrawlStrategyConfig[]>();
  if (configs) {
    for (const c of configs) {
      if (!domainMap.has(c.domain)) domainMap.set(c.domain, []);
      domainMap.get(c.domain)!.push(c);
    }
  }

  const domainEntries = Array.from(domainMap.entries())
    .filter(([domain]) => !filterText || domain.toLowerCase().includes(filterText.toLowerCase()))
    .sort((a, b) => {
      // * last, then alphabetical
      if (a[0] === "*") return 1;
      if (b[0] === "*") return -1;
      return a[0].localeCompare(b[0]);
    });

  if (configsLoading) {
    return (
      <div className="flex items-center justify-center h-64 gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Loading crawl strategies...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">爬取策略管理 Crawl Strategy</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
            onClick={() => clearAllPausesMutation.mutate()}
            disabled={clearAllPausesMutation.isPending}
          >
            {clearAllPausesMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
            解除全部封鎖
          </Button>
          <Button
            variant="outline"
            onClick={() => autoDetectMutation.mutate()}
            disabled={autoDetectMutation.isPending}
          >
            {autoDetectMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            Auto Detect Best
          </Button>
        </div>
      </div>

      {/* Domain Pause Mechanism Info Panel */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm">
        <div className="font-semibold text-blue-800 mb-2">⚙️ 底層封鎖機制說明 Domain Pause Mechanism (v0.38.11+)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-blue-100">
                <th className="border border-blue-200 px-3 py-1 text-left text-blue-900">動作</th>
                <th className="border border-blue-200 px-3 py-1 text-left text-red-700">舊機制（已廢棄）</th>
                <th className="border border-blue-200 px-3 py-1 text-left text-green-700">新機制（現行）✅</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-blue-200 px-3 py-1 font-medium">判斷方式</td>
                <td className="border border-blue-200 px-3 py-1 text-red-600">7 天內 ≥ 3 次失敗任務（SQL CTE）</td>
                <td className="border border-blue-200 px-3 py-1 text-green-700">domain_pauses 表 paused_until &gt; NOW()</td>
              </tr>
              <tr className="bg-blue-50/50">
                <td className="border border-blue-200 px-3 py-1 font-medium">觸發條件</td>
                <td className="border border-blue-200 px-3 py-1 text-red-600">任何 3 次失敗即觸發</td>
                <td className="border border-blue-200 px-3 py-1 text-green-700">P1~P7 所有策略全部耗盡才觸發</td>
              </tr>
              <tr>
                <td className="border border-blue-200 px-3 py-1 font-medium">暫停時間</td>
                <td className="border border-blue-200 px-3 py-1 text-red-600">永久（無解除機制）</td>
                <td className="border border-blue-200 px-3 py-1 text-green-700">2 小時（自動到期恢復）</td>
              </tr>
              <tr className="bg-blue-50/50">
                <td className="border border-blue-200 px-3 py-1 font-medium">解除方式</td>
                <td className="border border-blue-200 px-3 py-1 text-red-600">不可解除</td>
                <td className="border border-blue-200 px-3 py-1 text-green-700">右上角「解除全部封鎖」按鈕 ↗</td>
              </tr>
              <tr>
                <td className="border border-blue-200 px-3 py-1 font-medium">豁免域名</td>
                <td className="border border-blue-200 px-3 py-1 text-gray-500">—</td>
                <td className="border border-blue-200 px-3 py-1 text-green-700">alibaba.com / 1688.com（永不暫停）</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-blue-600 text-xs">
          💡 🚫 圖示：<span className="font-mono bg-blue-100 px-1">active</span> = 真正暫停中（可解除）｜
          <span className="font-mono bg-blue-100 px-1">historical</span> = 舊任務錯誤訊息殘留（不影響新任務）
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatsCard title="追蹤網域 Domains" value={totalDomains} icon={Globe} color="info" />
        <StatsCard title="平均成功率 Avg Success" value={`${avgSuccessRate}%`} icon={Activity} color="success" />
        <StatsCard title="今日爬取 Today" value={todayAttempts} icon={Zap} color="primary" />
        <StatsCard title="今日失敗率 Fail Rate" value={`${failureRate}%`} icon={AlertTriangle} color={Number(failureRate) > 30 ? "destructive" : "warning"} />
      </div>

      {/* Test Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">即時測試 Quick Test</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Domain (e.g., alibaba.com)"
              value={testDomain}
              onChange={(e) => setTestDomain(e.target.value)}
              className="w-48"
            />
            <Input
              placeholder="Full URL to test..."
              value={testUrl}
              onChange={(e) => setTestUrl(e.target.value)}
              className="flex-1"
            />
            <Button
              onClick={() => {
                if (!testDomain || !testUrl) {
                  toast.error("Enter domain and URL");
                  return;
                }
                testMutation.mutate({ domain: testDomain, url: testUrl });
              }}
              disabled={testMutation.isPending}
            >
              {testMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Domain Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">網域策略清單 Domain Strategies</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter domains..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Strategy</TableHead>
                <TableHead>Success Rate</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Rate Limit</TableHead>
                <TableHead>Last Tested</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domainEntries.map(([domain, domainConfigs]) => {
                const primary = domainConfigs[0];
                const isExpanded = expandedDomain === domain;
                const domainStat = stats?.find((s: DomainStats) => s.domain === domain);

                return (
                  <>
                    {/* Domain row */}
                    <TableRow
                      key={domain}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedDomain(isExpanded ? null : domain)}
                    >
                      <TableCell>
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </TableCell>
                      <TableCell className="font-medium">{domain === "*" ? "* (default)" : domain}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {domainConfigs.map((c) => (
                            <Badge key={c.id} variant="secondary" className={STRATEGY_COLORS[c.strategy] || ""}>
                              {c.strategy}{c.render_js ? " +JS" : ""}{c.premium_proxy ? " +Premium" : ""}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-muted rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${Number(primary.success_rate) > 70 ? "bg-green-500" : Number(primary.success_rate) > 40 ? "bg-yellow-500" : "bg-red-500"}`}
                              style={{ width: `${Math.min(Number(primary.success_rate), 100)}%` }}
                            />
                          </div>
                          <span className="text-sm">{Number(primary.success_rate).toFixed(1)}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{domainStat?.total_attempts ?? primary.total_attempts}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {primary.rate_limit_per_min}/min, {primary.rate_limit_delay_ms}ms
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {primary.last_tested_at
                          ? new Date(primary.last_tested_at).toLocaleString("zh-TW", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTestDomain(domain === "*" ? "" : domain);
                          }}
                        >
                          <Play className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>

                    {/* Expanded: strategy details */}
                    {isExpanded && domainConfigs.length > 1 && domainConfigs.slice(1).map((c) => (
                      <TableRow key={c.id} className="bg-muted/30">
                        <TableCell></TableCell>
                        <TableCell className="text-muted-foreground pl-8">↳ priority {c.priority}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={STRATEGY_COLORS[c.strategy] || ""}>
                            {c.strategy}{c.render_js ? " +JS" : ""}{c.premium_proxy ? " +Premium" : ""}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{Number(c.success_rate).toFixed(1)}%</span>
                        </TableCell>
                        <TableCell>{c.total_attempts}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {c.rate_limit_per_min}/min, {c.rate_limit_delay_ms}ms
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {c.last_tested_at
                            ? new Date(c.last_tested_at).toLocaleString("zh-TW", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ))}

                    {/* Expanded: logs */}
                    {isExpanded && (
                      <TableRow key={`${domain}-logs`} className="bg-muted/10">
                        <TableCell colSpan={8}>
                          <div className="py-2">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-medium">Recent Crawl Logs</h4>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => queryClient.invalidateQueries({ queryKey: ["crawl-strategy-logs", domain] })}
                              >
                                <RefreshCcw className="h-3 w-3 mr-1" />
                                Refresh
                              </Button>
                            </div>
                            {logsLoading ? (
                              <div className="flex items-center gap-2 py-4">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-sm">Loading logs...</span>
                              </div>
                            ) : logs && logs.length > 0 ? (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Time</TableHead>
                                    <TableHead>URL</TableHead>
                                    <TableHead>Strategy</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>HTTP</TableHead>
                                    <TableHead>Size</TableHead>
                                    <TableHead>Time</TableHead>
                                    <TableHead>Error</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(logs as CrawlAttemptLog[]).map((log) => (
                                    <TableRow key={log.id}>
                                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                        {new Date(log.created_at).toLocaleString("zh-TW", {
                                          month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
                                        })}
                                      </TableCell>
                                      <TableCell className="max-w-[200px] truncate text-xs" title={log.url}>
                                        {log.url}
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="secondary" className={`text-xs ${STRATEGY_COLORS[log.strategy] || ""}`}>
                                          {log.strategy}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="secondary" className={`text-xs ${STATUS_COLORS[log.status] || ""}`}>
                                          {log.status}
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-xs">{log.http_status || "—"}</TableCell>
                                      <TableCell className="text-xs">{formatBytes(log.html_size)}</TableCell>
                                      <TableCell className="text-xs">{formatTime(log.response_time_ms)}</TableCell>
                                      <TableCell className="max-w-[150px] truncate text-xs text-destructive" title={log.error_message || ""}>
                                        {log.error_message || "—"}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <p className="text-sm text-muted-foreground py-4">No logs yet for this domain.</p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
              {domainEntries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No strategies configured.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Strategy Matrix — domain × strategy grid */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base">網域策略矩陣 Strategy Matrix</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">V=有成功 X=全部失敗 —=從未嘗試 ⭐=已釘選首選策略</p>
            </div>
            <div className="flex gap-2 items-center">
              <div className="relative w-48">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter domains..."
                  value={matrixFilter}
                  onChange={(e) => setMatrixFilter(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => queryClient.invalidateQueries({ queryKey: ["crawl-strategy-matrix"] })}
              >
                <RefreshCcw className="h-3 w-3" />
              </Button>
              {resetAllConfirm ? (
                <div className="flex gap-1 items-center">
                  <span className="text-xs text-red-600">確認清空全部？</span>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-7 text-xs"
                    onClick={() => resetAllMutation.mutate()}
                    disabled={resetAllMutation.isPending}
                  >
                    {resetAllMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "確認"}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setResetAllConfirm(false)}>
                    取消
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 h-8 text-xs"
                  onClick={() => setResetAllConfirm(true)}
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  清空所有網域限制
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {matrixLoading ? (
            <div className="flex items-center gap-2 py-8 px-6">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">載入矩陣...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-muted/40 border-b">
                    <th className="text-left px-3 py-2 font-medium sticky left-0 bg-muted/40 min-w-[160px]">
                      Domain
                    </th>
                    {MATRIX_COLS.map((col, i) => (
                      <th key={col} className="px-1 py-2 font-medium text-center min-w-[52px] whitespace-nowrap">
                        <span className="text-[10px] text-muted-foreground">C{i + 1}</span>
                        <div className="text-[10px] leading-tight">{MATRIX_STRATEGY_LABELS[col]}</div>
                      </th>
                    ))}
                    <th className="px-2 py-2 font-medium text-center min-w-[80px]">釘選</th>
                    <th className="px-2 py-2 font-medium text-center min-w-[48px]">重置</th>
                  </tr>
                </thead>
                <tbody>
                  {(matrixData as MatrixRow[])
                    .filter((row) => !matrixFilter || row.domain.toLowerCase().includes(matrixFilter.toLowerCase()))
                    .map((row) => (
                      <tr key={row.domain} className={`border-b hover:bg-muted/20 ${row.is_paused ? 'bg-red-50/40' : ''}`}>
                        <td className="px-3 py-1.5 sticky left-0 bg-white dark:bg-background font-mono text-[11px] border-r">
                          {row.is_paused && <span className="mr-1 text-[10px]">🚫</span>}
                          {row.domain}
                        </td>
                        {MATRIX_COLS.map((col) => {
                          const cell = row.strategies[col];
                          const status = cell?.status ?? '—';
                          const isPinned = row.pinned_strategy === col;
                          return (
                            <td key={col} className="px-1 py-1">
                              <div className="flex justify-center">
                                <MatrixCell
                                  status={status}
                                  isPinned={isPinned}
                                  onClick={status === 'V' ? () => {
                                    if (isPinned) {
                                      unpinMutation.mutate(row.domain);
                                    } else {
                                      pinMutation.mutate({ domain: row.domain, strategy_name: col });
                                    }
                                  } : undefined}
                                />
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-2 py-1 text-center">
                          {row.pinned_strategy ? (
                            <div className="flex items-center justify-center gap-1">
                              <Star className="h-3 w-3 text-yellow-500 fill-yellow-400" />
                              <span className="text-[10px] text-yellow-700 font-mono truncate max-w-[60px]" title={row.pinned_strategy}>
                                {MATRIX_STRATEGY_LABELS[row.pinned_strategy as keyof typeof MATRIX_STRATEGY_LABELS] ?? row.pinned_strategy}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-1 py-1 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
                            onClick={() => resetDomainMutation.mutate(row.domain)}
                            title={`重置 ${row.domain}`}
                          >
                            <RefreshCcw className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  {matrixData.length === 0 && (
                    <tr>
                      <td colSpan={MATRIX_COLS.length + 3} className="text-center text-muted-foreground py-8">
                        尚無策略累積記錄（爬取任務執行後資料會逐步出現）
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Domain Crawl History — from domain_strategy_cache */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">網域爬取紀錄 Crawl History</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">各網域實際嘗試過的策略 P1~P7 成功/失敗次數</p>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter domains..."
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="w-8"></TableHead>
                <TableHead>Domain</TableHead>
                <TableHead className="text-center">任務數</TableHead>
                <TableHead className="text-center">完成</TableHead>
                <TableHead className="text-center">失敗</TableHead>
                <TableHead className="text-center">成功率</TableHead>
                <TableHead>最佳策略</TableHead>
                <TableHead className="text-center">狀態</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(domainHistory as TaskDomainStats[])
                .filter(d => !historyFilter || d.domain.toLowerCase().includes(historyFilter.toLowerCase()))
                .map((d) => {
                  const isExpH = expandedHistoryDomain === d.domain;
                  const stratEntries = Object.entries(d.strategies ?? {})
                    .sort((a, b) => {
                      // Sort: strategies with any success first, then by total desc
                      const aTotal = a[1].success + a[1].fail;
                      const bTotal = b[1].success + b[1].fail;
                      if (b[1].success !== a[1].success) return b[1].success - a[1].success;
                      return bTotal - aTotal;
                    });
                  const pct = d.success_rate;
                  const pctColor = pct >= 70 ? 'text-green-600' : pct >= 30 ? 'text-yellow-600' : 'text-red-500';
                  return (
                    <>
                      <TableRow
                        key={d.domain}
                        className={`cursor-pointer hover:bg-muted/40 ${d.is_blocklisted ? 'bg-red-50/40' : ''}`}
                        onClick={() => setExpandedHistoryDomain(isExpH ? null : d.domain)}
                      >
                        <TableCell>
                          {isExpH ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {d.is_blocklisted && <span className="text-[10px] bg-red-100 text-red-600 px-1 rounded mr-1">🚫</span>}
                          {d.domain}
                        </TableCell>
                        <TableCell className="text-center text-sm">{d.total_tasks}</TableCell>
                        <TableCell className="text-center text-sm text-green-600">{d.completed}</TableCell>
                        <TableCell className="text-center text-sm text-red-500">{d.failed}</TableCell>
                        <TableCell className="text-center">
                          <span className={`text-sm font-semibold ${pctColor}`}>{pct.toFixed(1)}%</span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {d.best_strategy
                            ? <Badge variant="secondary" className={`text-[10px] ${STRATEGY_COLORS[d.best_strategy.split(':')[0]] || ''}`}>
                                {d.best_strategy}
                              </Badge>
                            : '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {d.pending > 0
                            ? <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1 rounded">⏳{d.pending}</span>
                            : d.processing > 0
                              ? <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded">🔄{d.processing}</span>
                              : null}
                        </TableCell>
                      </TableRow>

                      {/* Expanded: strategy breakdown */}
                      {isExpH && (
                        <TableRow key={`${d.domain}-strat`} className="bg-muted/10">
                          <TableCell colSpan={8} className="py-3 px-6">
                            {stratEntries.length === 0 ? (
                              <p className="text-xs text-muted-foreground">尚無策略紀錄（domain_strategy_cache 尚未有此網域資料）</p>
                            ) : (
                              <div className="space-y-1.5">
                                <p className="text-xs font-medium text-muted-foreground mb-2">策略成功/失敗明細：</p>
                                {stratEntries.map(([stratName, counts]) => {
                                  const total = counts.success + counts.fail;
                                  const sPct = total > 0 ? (counts.success / total) * 100 : 0;
                                  return (
                                    <div key={stratName} className="flex items-center gap-3">
                                      <span className="text-[11px] font-mono w-52 shrink-0 text-muted-foreground">{stratName}</span>
                                      {/* Success bar */}
                                      <div className="flex items-center gap-1 flex-1">
                                        <div className="relative w-full max-w-[160px] h-3 bg-red-100 rounded-full overflow-hidden">
                                          <div
                                            className="absolute left-0 top-0 h-full bg-green-500 rounded-full"
                                            style={{ width: `${sPct}%` }}
                                          />
                                        </div>
                                        <span className="text-[11px] w-10 text-right">
                                          {sPct > 0 ? `${sPct.toFixed(0)}%` : '0%'}
                                        </span>
                                      </div>
                                      <span className="text-[11px] text-green-600 flex items-center gap-0.5">
                                        <CheckCircle2 className="h-3 w-3" />{counts.success}
                                      </span>
                                      <span className="text-[11px] text-red-500 flex items-center gap-0.5">
                                        <XCircle className="h-3 w-3" />{counts.fail}
                                      </span>
                                      {counts.avg_ms > 0 && (
                                        <span className="text-[10px] text-muted-foreground">{(counts.avg_ms / 1000).toFixed(1)}s</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })}
              {domainHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" />載入中...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
